import { Link, Typography } from '@mui/material'
import React from 'react'

/**
 * Simple markdown parser for basic formatting
 * Supports: h1, h2, h3, bold, and links
 */

interface MarkdownElement {
  type: 'h1' | 'h2' | 'h3' | 'text'
  content: React.ReactNode
}

/**
 * Parse inline markdown (bold and links) within a line
 */
function parseInlineMarkdown(text: string): React.ReactNode[] {
  const elements: React.ReactNode[] = []
  let currentIndex = 0

  // Regex patterns
  const boldPattern = /\*\*(.+?)\*\*/g
  const linkPattern = /\[([^\]]+)\]\(([^)]+)\)/g

  // Combine patterns to find all matches
  const allMatches: Array<{
    index: number
    length: number
    type: 'bold' | 'link'
    content: string
    url?: string
  }> = []

  // Find all bold matches
  let match
  while ((match = boldPattern.exec(text)) !== null) {
    allMatches.push({
      index: match.index,
      length: match[0].length,
      type: 'bold',
      content: match[1]
    })
  }

  // Find all link matches
  while ((match = linkPattern.exec(text)) !== null) {
    allMatches.push({
      index: match.index,
      length: match[0].length,
      type: 'link',
      content: match[1],
      url: match[2]
    })
  }

  // Sort matches by index
  allMatches.sort((a, b) => a.index - b.index)

  // Build the result
  allMatches.forEach((match, idx) => {
    // Add text before this match
    if (match.index > currentIndex) {
      elements.push(text.substring(currentIndex, match.index))
    }

    // Add the formatted element
    if (match.type === 'bold') {
      elements.push(
        <strong key={`bold-${idx}`} style={{ fontWeight: 600 }}>
          {match.content}
        </strong>
      )
    } else if (match.type === 'link') {
      elements.push(
        <Link
          key={`link-${idx}`}
          href={match.url}
          target='_blank'
          rel='noopener noreferrer'
          sx={{ color: 'primary.main' }}
        >
          {match.content}
        </Link>
      )
    }

    currentIndex = match.index + match.length
  })

  // Add remaining text
  if (currentIndex < text.length) {
    elements.push(text.substring(currentIndex))
  }

  return elements.length > 0 ? elements : [text]
}

/**
 * Parse markdown text into structured elements
 */
export function parseMarkdown(text: string): MarkdownElement[] {
  const lines = text.split('\n')
  const elements: MarkdownElement[] = []

  lines.forEach(line => {
    const trimmedLine = line.trim()

    if (trimmedLine.startsWith('# ')) {
      // H1
      const content = trimmedLine.substring(2)
      elements.push({
        type: 'h1',
        content: parseInlineMarkdown(content)
      })
    } else if (trimmedLine.startsWith('## ')) {
      // H2
      const content = trimmedLine.substring(3)
      elements.push({
        type: 'h2',
        content: parseInlineMarkdown(content)
      })
    } else if (trimmedLine.startsWith('### ')) {
      // H3
      const content = trimmedLine.substring(4)
      elements.push({
        type: 'h3',
        content: parseInlineMarkdown(content)
      })
    } else {
      // Regular text
      elements.push({
        type: 'text',
        content: parseInlineMarkdown(line)
      })
    }
  })

  return elements
}

/**
 * Render parsed markdown elements as React components
 */
export function renderMarkdown(text: string): React.ReactNode {
  const elements = parseMarkdown(text)

  return elements.map((element, index) => {
    switch (element.type) {
      case 'h1':
        return (
          <Typography
            key={index}
            variant='h4'
            component='h1'
            sx={{ fontWeight: 700, mb: 2, mt: index > 0 ? 3 : 0 }}
          >
            {element.content}
          </Typography>
        )
      case 'h2':
        return (
          <Typography
            key={index}
            variant='h5'
            component='h2'
            sx={{ fontWeight: 600, mb: 1.5, mt: index > 0 ? 2.5 : 0 }}
          >
            {element.content}
          </Typography>
        )
      case 'h3':
        return (
          <Typography
            key={index}
            variant='h6'
            component='h3'
            sx={{ fontWeight: 600, mb: 1, mt: index > 0 ? 2 : 0 }}
          >
            {element.content}
          </Typography>
        )
      case 'text':
        return (
          <Typography
            key={index}
            variant='body1'
            sx={{ mb: 1, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
          >
            {element.content}
          </Typography>
        )
      default:
        return null
    }
  })
}
